import { ORG_ROLES, type Role } from '../constants/business/user';
import type { Permission } from './permissions';

/**
 * Baseline permissions granted to every standard organisation user.
 *
 * Both `user` and `shared_edit_user` derive their permissions from this list:
 * - `user` is exactly this list.
 * - `shared_edit_user` is this list plus the handling-party update deltas and
 *   the broader `training_record:create` permission (see ROLE_PERMISSIONS).
 *
 * Treat this as the source of truth for "what a standard user can do". The
 * `shared_edit_user inherits standard user permissions` test in roles.test.ts
 * locks in the superset invariant so the two roles cannot drift apart.
 */
const STANDARD_USER_PERMISSIONS = [
    // Claim management
    'claim:create',
    'claim:read:own_handling_parties',
    'claim:update:own_handling_parties',
    'claim:update:is_owner',

    // Claim Decision Engine
    'claim_decision_engine:use',

    // Contact management
    'contact:read',

    // Complaint management
    'complaint:create',
    'complaint:read:own_handling_parties_or_is_owner',
    'complaint:update:is_owner',

    // Incident management
    'incident:create',
    'incident:read:own_handling_parties',
    'incident:read:owner_without_handling_parties',
    'incident:update:is_owner',

    // Obligation management
    'obligation:read:own_handling_parties',
    'obligation:update:is_owner',

    // Financial section
    'financial_section:read',

    // Transaction type
    'transaction_type:read',

    // Cost code
    'cost_code:read',

    // Expense type override
    'expense_type_override:read',
    'expense_type_override:create',
    'expense_type_override:update',
    'expense_type_override:delete',

    // Billing office
    'billing_office:read',
    'billing_office:create',
    'billing_office:update',
    'billing_office:delete',

    // Training record
    'training_record:read',
    'training_record:create:own_training',
    'training_record:update',
    'training_record:delete',

    // Report
    'report:read:created_by_user',
    'report:delete:created_by_user',

    // FAQ
    'faq:read',

    // Payment user
    'payment_user:read',

    // Material service provider
    'msp:create',
    'msp:read:own_handling_parties',
    'msp:read:owner_without_handling_parties',
    'msp:update:is_owner',

    // MSP Governance Check
    'msp_governance_check:update:is_owner',
    'msp_governance_check:delete:is_owner',

    // Settings
    'settings:read',

    // Control
    'control:create',
    'control:read:own_handling_parties',
    'control:read:owner_without_handling_parties',
    'control:update:is_owner',

    // Risk
    'risk:create',
    'risk:read:own_handling_parties',
    'risk:read:owner_without_handling_parties',
    'risk:update:is_owner',

    // Register permissions are deliberately absent (CUR-857): registers hold
    // sensitive content (fit and proper checks, conduct matters), so access is
    // restricted to org_admin until a finer-grained permissions model exists.

    // Risk action plan
    'risk_action_plan:create',
    'risk_action_plan:update:is_owner',
    'risk_action_plan:read:is_owner',

    // AI assistant
    'ai_assistant:use',

    // Supplier
    'supplier:read',
    'supplier:create',

    // Communication template
    'comm_template:read',

    // Communication signature
    'comm_signature:read',

    // Conflict of Interest management
    'conflict_of_interest:create',
    'conflict_of_interest:read:own_handling_parties_or_is_owner',
    'conflict_of_interest:update:is_owner',

    // Form schema
    'form_schema:read',

    // Compliance audit
    'compliance_audit:access',
] as const satisfies readonly Permission[];

/**
 * Role-based permission mappings
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
    super_admin: [
        'super:admin',

        // Admin Console Module Permissions - super_admin has ALL admin permissions
        'admin_orgs:read',
        'admin_orgs:update',
        'admin_orgs:create',
        'admin_users:full',
        'admin_users:read',
        'admin_content:full',
        'admin_content:read',
        'admin_comms:full',
        'admin_comms:read',
        'admin_ai:full',
        'admin_ai:read',
        'admin_ai:raw_logs',
        'admin_billing:full',
        'admin_billing:read',
        'admin_system:full',
        'admin_system:read',
        'admin_ops_read:full',
        'admin_support:full',
        'admin_support:read',

        // User management
        'user:create',
        'user:read',
        'user:update',

        // Org
        'org:update',

        // AI assistant
        'ai_assistant:use',

        // Communication template
        'comm_template:read',
        'comm_template:create',
        'comm_template:update',
        'comm_template:delete',

        // Communication signature
        'comm_signature:read',
        'comm_signature:create',
        'comm_signature:update',
        'comm_signature:delete',

        // Configuration
        'settings:update',
        'settings:read',
        'saved_view:share',

        // Audit trail
        'audit_trail:read',
    ],

    // Read-only Admin Console Role
    // Has read-only access to all admin console modules
    admin_read_only: [
        'admin_orgs:read',
        'admin_users:read',
        'admin_content:read',
        'admin_comms:read',
        'admin_ai:read',
        'admin_billing:read',
        'admin_system:read',
        'admin_support:read',

        // Audit trail
        'audit_trail:read',
    ],

    // Granular Admin Console Roles
    // Each role has full + read permissions for their module
    admin_org_manager: [
        'admin_orgs:read',
        'admin_orgs:update',
        'admin_orgs:create',
        'admin_billing:full',
        'admin_billing:read',
    ],
    admin_user_manager: ['admin_users:full', 'admin_users:read'],
    admin_content_manager: ['admin_content:full', 'admin_content:read'],
    admin_comms_manager: ['admin_comms:full', 'admin_comms:read'],
    admin_ai_manager: ['admin_ai:full', 'admin_ai:read'],
    admin_system_ops: ['admin_system:full', 'admin_system:read'],
    // Read-only cross-org ops debugging (ops-read MCP). Deliberately a single :full
    // permission with no :read twin — the capability is already read-only by
    // construction, and it must never be folded into admin_read_only (it reaches
    // real tenant data across orgs).
    admin_ops_reader: ['admin_ops_read:full'],

    // Support session management role
    admin_support_manager: [
        'admin_support:full',
        'admin_support:read',
        'admin_orgs:read', // Need to view org list to select target org
    ],

    // Support user account role (support staff who can be granted temp org access)
    // Note: Support users don't need many admin permissions - their access is granted via support sessions
    admin_support_user: [],

    org_admin: [
        // Organization Administration
        'org:admin',

        // MCP access management
        'mcp_access:manage',

        // Audit trail
        'audit_trail:read',

        // User management
        'user:create',
        'user:read',
        'user:update',

        // Task management
        'task:manage',
        'task:reassign:selected',

        // Claim management
        'claim:create',
        'claim:read',
        'claim:update',

        // Claim Decision Engine
        'claim_decision_engine:manage',
        'claim_decision_engine:use',

        // Contact management
        'contact:read',
        'contact:create',
        'contact:update',
        'contact:delete',

        // Complaint management
        'complaint:create',
        'complaint:read',
        'complaint:update',

        // Incident management
        'incident:create',
        'incident:read',
        'incident:update',

        // Obligation management
        'obligation:read',
        'obligation:read:own_handling_parties',
        'obligation:update',

        // Billing
        'billing:read',
        'billing:add_funds',

        // Supplier
        'supplier:read',
        'supplier:create',
        'supplier:update',
        'supplier:delete',

        // Product type
        'product_type:create',
        'product_type:update',
        'product_type:delete',

        // Financial section
        'financial_section:read',
        'financial_section:update',

        // Transaction type
        'transaction_type:read',

        // Cost code
        'cost_code:read',

        // PDS
        'pds:read',
        'pds:create',
        'pds:update',

        // PDS version
        'pds_version:read',
        'pds_version:create',
        'pds_version:update',

        // Insurer
        'insurer:read',
        'insurer:create',
        'insurer:update',
        'insurer:delete',

        // Indicator definition
        'indicator_definition:read',
        'indicator_definition:create',
        'indicator_definition:update',
        'indicator_definition:delete',

        // Expense type
        'expense_type:read',
        'expense_type:create',
        'expense_type:update',
        'expense_type:delete',

        // Risk category
        'risk_category:read',
        'risk_category:create',
        'risk_category:update',
        'risk_category:delete',

        // Risk action plan
        'risk_action_plan:read',
        'risk_action_plan:create',
        'risk_action_plan:update',
        'risk_action_plan:delete',

        // Questionnaire
        'questionnaire:read',
        'questionnaire:create',
        'questionnaire:update',
        'questionnaire:delete',

        // Training record
        'training_record:read',
        'training_record:create',
        'training_record:update',
        'training_record:delete',

        // Report
        'report:read:created_by_user',
        'report:delete:created_by_user',

        // Register
        'register:read',
        'register:update',

        // FAQ
        'faq:read',

        // Org
        'org:update',

        // Training period
        'training_period:read',
        'training_period:create',
        'training_period:update',
        'training_period:delete',

        // Payment user
        'payment_user:read',
        'payment_user:create',
        'payment_user:update',
        'payment_user:delete',

        // Settings
        'settings:read',
        'settings:update',
        'saved_view:share',
        'inbound_email_archive:read',

        // Material service provider
        'msp:read',
        'msp:create',
        'msp:update',

        // MSP Governance Check
        'msp_governance_check:update:is_owner',
        'msp_governance_check:delete:is_owner',

        // Control
        'control:read',
        'control:create',
        'control:update',
        'control:delete',

        // Risk
        'risk:read',
        'risk:create',
        'risk:update',
        'risk:delete',

        // AI assistant
        'ai_assistant:use',

        // Communication template
        'comm_template:read',
        'comm_template:create',
        'comm_template:update',
        'comm_template:delete',

        // Communication signature
        'comm_signature:read',
        'comm_signature:create',
        'comm_signature:update',
        'comm_signature:delete',

        // Document analyzer
        'document_analyzer:use',

        // Conflict of Interest management
        'conflict_of_interest:create',
        'conflict_of_interest:read',
        'conflict_of_interest:update',

        // Form schema
        'form_schema:read',
        'form_schema:create',
        'form_schema:update',
        'form_schema:delete',

        // User attestation management
        'user_attestation:read',
        'user_attestation:create',
        'user_attestation:update',

        // Support session management
        'support_session:read',
        'support_session:approve',

        // Service API Token management
        'service_api_token:read',
        'service_api_token:create',
        'service_api_token:update',
        'service_api_token:delete',

        // Market PDS
        'market_pds:access',

        // Compliance audit
        'compliance_audit:access',
    ],

    user: [...STANDARD_USER_PERMISSIONS],

    supplier: [],

    user_manager: ['user:create', 'user:read', 'user:update'],

    auto_detect_user: [
        // Auto-detect tool — deliberately the ONLY role with message-classification
        // permissions. Access is all-or-nothing: this role fully operates the tool
        // (inbox, review, rerun, vulnerability triage); nobody else, org admins
        // included, can use it without also holding this role.
        'message_classification:access',
        'message_classification:create',
        'message_classification:read',
        'message_classification:update',
        'message_classification:delete',
        'message_classification:vulnerability:view',
        'message_classification:vulnerability:manage',
        'message_classification:vulnerability:view_sensitive',
    ],

    website_checker_user: [
        // Website checker
        'website_checker:access',
    ],

    market_pds_user: [
        // Market PDS comparison tool
        'market_pds:access',
    ],

    shared_edit_user: [
        ...STANDARD_USER_PERMISSIONS,

        // Shared Edit Users can also update records via handling-party membership
        // (not just ownership) — the role's defining capability.
        'complaint:update:own_handling_parties',
        'incident:update:own_handling_parties',
        'obligation:update:own_handling_parties',
        'msp:update:own_handling_parties',
        'control:update:own_handling_parties',
        'risk:update:own_handling_parties',
        'conflict_of_interest:update:own_handling_parties',

        // Broader training record creation. The narrower
        // `training_record:create:own_training` is already included via
        // STANDARD_USER_PERMISSIONS; both literals coexist intentionally because
        // permission strings are compared verbatim by `hasPermission`, and code
        // paths may check either qualifier.
        'training_record:create',
    ],

    training_manager: [
        // Training record
        'training_record:read',
        'training_record:create',
        'training_record:update',
        'training_record:delete',

        // Training period
        'training_period:read',

        // Report
        'report:read:created_by_user',
        'report:delete:created_by_user',

        // FAQ
        'faq:read',

        // Settings
        'settings:read',
    ],

    attestations_user: [
        // User attestation management
        'user_attestation:read',
        'user_attestation:create',
        'user_attestation:update',

        // Form schema (read-only for viewing attestation forms)
        'form_schema:read',
    ],

    // External fleet-manager portal access (CUR-505). Deliberately NOT built on
    // STANDARD_USER_PERMISSIONS — portal users are client-side people, not tenant
    // staff, and must not hold claim/incident/complaint create or update grants.
    fleet_portal_user: ['fleet_portal:access', 'fleet_portal:read', 'fleet_authorisation:approve'],
};

/**
 * Check if a user has a specific permission
 */
export function hasPermission(userRoles: Role[], permission: Permission) {
    return userRoles.some((role) => ROLE_PERMISSIONS[role]?.includes(permission));
}

const CONSOLE_WORKSPACE_ROLES = new Set<Role>([
    ORG_ROLES.ORG_ADMIN,
    ORG_ROLES.USER_MANAGER,
    ORG_ROLES.USER,
    ORG_ROLES.SHARED_EDIT_USER,
    ORG_ROLES.TRAINING_MANAGER,
    ORG_ROLES.ATTESTATIONS_USER,
]);

/** True when at least one role grants access to the organisation console workspace. */
export function hasConsoleWorkspaceAccess(userRoles: Role[]): boolean {
    return userRoles.some((role) => CONSOLE_WORKSPACE_ROLES.has(role));
}

/** True when a fleet-portal user has no role that grants access to the console workspace. */
export function isFleetPortalOnlyUser(userRoles: Role[]): boolean {
    return userRoles.includes(ORG_ROLES.FLEET_PORTAL_USER) && !hasConsoleWorkspaceAccess(userRoles);
}
